import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Trash2, CheckCircle2, Clock, XCircle, Eye, Copy, ExternalLink, FileText, ClipboardCheck, Wallet, Mail, Building2, AlertCircle, Rocket } from "lucide-react";
import { DevisProposalForm } from "@/components/admin/DevisProposalForm";

const PAYMENT_META = {
  pending: { label: "⏳ En attente paiement", cls: "bg-amber-50 text-amber-800 border-amber-300" },
  paid: { label: "✅ Payé", cls: "bg-emerald-50 text-emerald-800 border-emerald-300" },
  failed: { label: "❌ Paiement échoué", cls: "bg-red-50 text-red-800 border-red-300" },
  commercial_request: { label: "💼 Demande commerciale", cls: "bg-sky-50 text-sky-800 border-sky-300" },
};

const STATUS = {
  pending: { label: "Nouveau", cls: "status-pending" },
  in_review: { label: "En cours", cls: "status-pending" },
  approved: { label: "Validé", cls: "status-active" },
  rejected: { label: "Refusé", cls: "status-blocked" },
};

const KIND_META = {
  inscription: { label: "Inscription", bg: "bg-[#002FA7] text-white", desc: "Adhésion via formulaire principal" },
  devis: { label: "Devis", bg: "bg-[#FFD700] text-[#0A0A0A]", desc: "Demande de tarification personnalisée" },
};

const TABS = [
  { key: "all", label: "Toutes" },
  { key: "inscription", label: "Inscriptions" },
  { key: "devis", label: "Devis" },
];

export default function AdminInscriptions() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [tab, setTab] = useState("all");

  const load = async () => {
    try { const { data } = await api.get("/admin/inscriptions"); setItems(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (i, status) => {
    try {
      const path = i.kind === "devis" ? `/admin/devis/${i.id}` : `/admin/inscriptions/${i.id}`;
      await api.patch(path, { status, note });
      toast.success("Statut mis à jour");
      // Si devis validé → ouvrir mailto avec réponse pré-remplie (F1)
      if (i.kind === "devis" && status === "approved" && i.email) {
        const subject = encodeURIComponent(`PAG Business Manager — Votre devis "${i.raison_sociale}"`);
        const bodyLines = [
          `Bonjour ${i.prenom || i.chef_prenom || ""} ${i.nom || i.chef_nom || ""},`,
          ``,
          `Merci pour votre demande de devis. Suite à l'étude de votre besoin :`,
          `• Structure : ${i.type_structure || "—"}`,
          `• Activité : ${i.activite || "—"}`,
          `• Salariés : ${i.nb_salaries || "?"}`,
          `• Budget souhaité : ${i.budget_range || "—"}`,
          `• Paiement : ${i.paiement_souhaite || "—"}`,
          ``,
          `Nous vous proposons la formule suivante :`,
          `[à compléter — plan + prix]`,
          ``,
          note ? `Note interne : ${note}` : ``,
          ``,
        `Le paiement est effectué directement via PayPal. Souhaitez-vous que je vous envoie un lien de paiement PayPal personnalisé ?`,
          ``,
          `À votre écoute,`,
          `L'équipe PAG Business Manager`,
          `contact@parcavenue.eu · 09 70 70 17 92`,
        ].filter(Boolean).join("\n");
        const mailto = `mailto:${i.email}?subject=${subject}&body=${encodeURIComponent(bodyLines)}`;
        window.location.href = mailto;
      }
      setSelected(null); setNote("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (i) => {
    if (!window.confirm("Supprimer cette demande ?")) return;
    try {
      const path = i.kind === "devis" ? `/admin/devis/${i.id}` : `/admin/inscriptions/${i.id}`;
      await api.delete(path);
      toast.success("Supprimé"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const genPaypalLink = async (i) => {
    try {
      const { data } = await api.post(`/admin/inscriptions/${i.id}/paypal-link`);
      if (!data.approval_url) throw new Error("Pas de lien PayPal reçu");
      await navigator.clipboard.writeText(data.approval_url);
      toast.success(data.cached ? "Lien PayPal existant copié" : "Lien PayPal généré et copié");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const filtered = useMemo(() => {
    if (tab === "all") return items;
    return items.filter((i) => (i.kind || "inscription") === tab);
  }, [items, tab]);

  const counts = useMemo(() => {
    const c = { pending: 0, in_review: 0, approved: 0, rejected: 0, inscription: 0, devis: 0 };
    for (const i of items) {
      c[i.status] = (c[i.status] || 0) + 1;
      c[i.kind || "inscription"] = (c[i.kind || "inscription"] || 0) + 1;
    }
    return c;
  }, [items]);

  return (
    <div className="min-h-full">
      <PageHeader eyebrow="Demandes" title="Inscriptions & Devis" sub="Inscriptions d'entreprises et demandes de devis sur mesure — reçues via le site public." />
      <div className="p-6 lg:p-10 space-y-6">
        <div className="p-4 border-l-4 border-[#002FA7] bg-[#002FA7]/5" data-testid="admin-payment-rule-banner">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-[#002FA7] flex-shrink-0 mt-0.5" />
            <div className="text-sm text-[#0A0A0A]">
              <b>Règle stricte v2.0.05 :</b> aucun espace entreprise n'est créé tant que le paiement PayPal
              n'est pas confirmé (événement <code className="bg-white px-1 border">SUBSCRIPTION.ACTIVATED</code>).
              Les inscriptions en <b>⏳ En attente paiement</b> ne peuvent pas se connecter — elles n'apparaissent
              nulle part côté chef tant que la transaction n'aboutit pas. <b>Stripe côté client a été retiré</b> ;
              seul PayPal est proposé au client final.
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          <Stat label="Nouveaux" value={counts.pending || 0} icon={Clock} accent="#FFD700" testid="stat-pending" />
          <Stat label="En cours" value={counts.in_review || 0} icon={Eye} accent="#0066FF" testid="stat-review" />
          <Stat label="Validés" value={counts.approved || 0} icon={CheckCircle2} accent="#059669" testid="stat-approved" />
          <Stat label="Refusés" value={counts.rejected || 0} icon={XCircle} accent="#E60000" testid="stat-rejected" />
          <Stat label="Inscriptions" value={counts.inscription || 0} icon={ClipboardCheck} accent="#002FA7" testid="stat-inscription" />
          <Stat label="Devis" value={counts.devis || 0} icon={FileText} accent="#FFD700" testid="stat-devis" />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-[#DEE2E6]">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`px-4 h-11 text-xs uppercase tracking-[0.2em] font-bold transition-colors border-b-2 -mb-px ${tab === t.key ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-[#868E96] hover:text-[#0A0A0A]"}`}
            >
              {t.label}
              <span className="ml-2 px-2 py-0.5 bg-[#F1F3F5] rounded text-[10px] text-[#495057]">
                {t.key === "all" ? items.length : (counts[t.key] || 0)}
              </span>
            </button>
          ))}
        </div>

        <div className="bg-white border border-[#DEE2E6]" data-testid="inscriptions-list">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#868E96]">Aucune demande dans cet onglet.</div>
          ) : filtered.map((i) => {
            const kind = i.kind || "inscription";
            const km = KIND_META[kind];
            return (
              <div key={`${kind}-${i.id}`} className="p-5 border-b border-[#DEE2E6] last:border-b-0 grid lg:grid-cols-12 gap-4 items-start" data-testid={`inscription-${i.id}`}>
                <div className="lg:col-span-3">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`text-[9px] uppercase tracking-[0.15em] px-2 py-0.5 font-bold ${km.bg}`} data-testid={`kind-badge-${i.id}`}>
                      {km.label}
                    </span>
                    <span className={`status-pill ${STATUS[i.status]?.cls || ""}`}>{STATUS[i.status]?.label || i.status}</span>
                    {i.en_projet && <span className="text-[9px] uppercase tracking-[0.15em] px-2 py-0.5 font-bold bg-[#FFD700] text-[#0A0A0A] inline-flex items-center gap-1" data-testid={`projet-badge-${i.id}`}><Rocket className="h-3 w-3" /> Projet · sans SIRET</span>}
                  </div>
                  {/* Badge paiement + activation (v2.0.04) */}
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span
                      className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border font-bold ${PAYMENT_META[i.payment_status_computed]?.cls || PAYMENT_META.pending.cls}`}
                      data-testid={`payment-badge-${i.id}`}
                    >
                      {PAYMENT_META[i.payment_status_computed]?.label || i.payment_status_computed}
                    </span>
                    {i.entreprise_activated && (
                      <span
                        className="text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border font-bold bg-[#002FA7] text-white border-[#002FA7] flex items-center gap-1"
                        data-testid={`activated-badge-${i.id}`}
                      >
                        <Building2 className="h-3 w-3" /> Compte : {i.entreprise_code_activated}
                      </span>
                    )}
                    {i.bp_attribution && (
                      <span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border font-bold ${i.bp_attribution.conflict ? "bg-[#E60000] text-white border-[#E60000]" : "bg-[#FFD700] text-[#0A0A0A] border-[#FFD700]"}`}
                            title={i.bp_attribution.conflict ? "Deux partenaires revendiquent cette vente" : `Attribution : ${i.bp_attribution.method}`} data-testid={`bp-badge-${i.id}`}>
                        BP : {i.bp_attribution.conflict ? "conflit à trancher" : i.bp_attribution.bp_code}
                      </span>
                    )}
                  </div>
                  <div className="font-display font-bold">{i.raison_sociale}</div>
                  <div className="text-xs text-[#495057]">{i.chef_prenom || i.prenom} {i.chef_nom || i.nom}</div>
                  <div className="text-xs text-[#868E96] font-mono">{i.email}</div>
                  <div className="text-xs text-[#868E96] font-mono">{i.telephone}</div>
                </div>

                <div className="lg:col-span-3 text-xs">
                  {kind === "inscription" ? (
                    <>
                      <div><b>Plan:</b> {i.plan} ({i.cycle})</div>
                      <div><b>Secteur:</b> {i.secteur || "—"}</div>
                      <div><b>Salariés:</b> {i.nb_salaries_estime || "?"}</div>
                      <div><b>SIRET:</b> {i.siret || (i.en_projet ? "— (entreprise en projet)" : "—")}</div>
                    </>
                  ) : (
                    <>
                      <div><b>Type demande:</b> {i.type_demande || "—"}</div>
                      <div><b>Structure:</b> {i.type_structure || "—"}</div>
                      <div><b>Activité:</b> {i.activite || "—"}</div>
                      <div><b>Salariés:</b> {i.nb_salaries || "?"}</div>
                      <div><b>Budget:</b> {i.budget_range || "—"}</div>
                      <div><b>Paiement:</b> {i.paiement_souhaite || "—"}</div>
                      <div data-testid={`devis-locale-${i.id}`}><b>Langue / devise:</b> {(i.language || "fr").toUpperCase()} · {i.currency || "EUR"}{i.currency && i.currency !== "EUR" && i.currency_rate ? ` (1 € = ${i.currency_rate} ${i.currency})` : ""}</div>
                      {i.nb_etablissements && <div><b>Établissements:</b> {i.nb_etablissements}</div>}
                      {i.siret && <div><b>SIRET:</b> {i.siret}</div>}
                    </>
                  )}
                </div>

                <div className="lg:col-span-2 text-xs text-[#495057] italic">"{i.message || i.besoin_detail || "—"}"</div>

                <div className="lg:col-span-2">
                  <div className="text-[10px] text-[#868E96]">{new Date(i.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</div>
                  {i.status === "approved" && kind === "inscription" && (
                    <div className="mt-2 flex flex-col gap-1" data-testid={`pay-page-block-${i.id}`}>
                      <div className="text-[9px] uppercase tracking-wider font-bold text-[#059669]">Page paiement PayPal</div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" data-testid={`pay-page-copy-${i.id}`} onClick={() => {
                          const url = `${window.location.origin}/pay/checkout/${i.id}`;
                          navigator.clipboard.writeText(url);
                          toast.success("Lien de paiement copié");
                        }} className="h-7 text-[10px] flex-1"><Copy className="h-3 w-3 mr-1" />Copier lien</Button>
                        <a href={`/pay/checkout/${i.id}`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" data-testid={`pay-page-open-${i.id}`} className="h-7 px-2"><ExternalLink className="h-3 w-3" /></Button></a>
                      </div>
                    </div>
                  )}
                  {kind === "inscription" && (
                    <div className="mt-2 flex flex-col gap-1" data-testid={`paypal-block-${i.id}`}>
                      <div className="text-[9px] uppercase tracking-wider font-bold text-[#0070BA]">Lien PayPal direct</div>
                      {i.paypal_approval_url ? (
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" data-testid={`paypal-copy-${i.id}`} onClick={() => { navigator.clipboard.writeText(i.paypal_approval_url); toast.success("Lien PayPal copié"); }} className="h-7 text-[10px] flex-1"><Copy className="h-3 w-3 mr-1" />Copier</Button>
                          <a href={i.paypal_approval_url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" data-testid={`paypal-open-${i.id}`} className="h-7 px-2"><ExternalLink className="h-3 w-3" /></Button></a>
                        </div>
                      ) : (
                        <Button size="sm" onClick={() => genPaypalLink(i)} data-testid={`paypal-gen-${i.id}`} className="h-7 text-[10px] bg-[#0070BA] hover:bg-[#005a94] text-white"><Wallet className="h-3 w-3 mr-1" />Générer PayPal</Button>
                      )}
                      {i.paypal_amount && <div className="text-[10px] font-mono text-[#0070BA]">{i.paypal_amount.toFixed(2)} € ({i.paypal_cycle})</div>}
                    </div>
                  )}
                </div>

                <div className="lg:col-span-2 flex gap-1 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => setSelected(selected === i.id ? null : i.id)} data-testid={`process-${i.id}`} className="h-8 text-xs">{selected === i.id ? "Fermer" : "Traiter"}</Button>
                  <Button size="sm" variant="outline" onClick={() => remove(i)} className="h-8 border-[#E60000] text-[#E60000] hover:bg-[#E60000] hover:text-white" data-testid={`delete-${i.id}`}><Trash2 className="h-3 w-3" /></Button>
                </div>

                {selected === i.id && (
                  <div className="lg:col-span-12 p-4 bg-[#F8F9FA] border border-[#DEE2E6] mt-2 space-y-2">
                    <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note interne (optionnel)" rows={2} data-testid={`note-${i.id}`} />
                    <div className="flex gap-2 flex-wrap">
                      <Button size="sm" onClick={() => updateStatus(i, "in_review")} data-testid={`set-review-${i.id}`} className="bg-[#0066FF] text-white">En cours</Button>
                      <Button size="sm" onClick={() => updateStatus(i, "approved")} data-testid={`set-approve-${i.id}`} className="bg-[#059669] text-white">
                        {kind === "inscription" ? "Valider (→ PayPal)" : (<><Mail className="h-3 w-3 mr-1" /> Marquer répondu (mail)</>)}
                      </Button>
                      <Button size="sm" onClick={() => updateStatus(i, "rejected")} data-testid={`set-reject-${i.id}`} variant="outline" className="border-[#E60000] text-[#E60000]">Refuser</Button>
                    </div>
                    {i.note && <div className="text-xs text-[#495057]"><b>Note:</b> {i.note}</div>}
                  </div>
                )}
                {kind === "devis" && (selected === i.id || i.proposal) && <DevisProposalForm devis={i} onDone={load} />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className="bg-white border border-[#DEE2E6] p-4" data-testid={testid}>
      <Icon className="h-4 w-4 mb-2" style={{ color: accent }} />
      <div className="font-display text-3xl font-black tracking-tighter">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#868E96] font-semibold mt-1">{label}</div>
    </div>
  );
}
